import React from 'react';
import PropTypes from 'prop-types';
import { Box, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import SearchIcon from '@mui/icons-material/Search';
import PercentIcon from '@mui/icons-material/Percent';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import ConfigCard from './ConfigCard';

// Toggleable items rendered as the top "icon + label + switch" rows.
// Brands, Shop the look, Outlet & Gift voucher are also configured per-page
// in their own modules — toggling them here only controls header visibility.
const TOGGLE_ITEMS = [
    { key: 'contact',     i18nKey: 'cfg_nav_header_toggle_contact',      fallback: 'Contact',         icon: ChatBubbleOutlineIcon },
    { key: 'brands',      i18nKey: 'cfg_nav_header_toggle_brands',       fallback: 'Brands',          icon: LocalOfferIcon        },
    { key: 'shopTheLook', i18nKey: 'cfg_nav_header_toggle_shop_the_look',fallback: 'Shop the look',   icon: CheckroomIcon         },
    { key: 'outlet',      i18nKey: 'cfg_nav_header_toggle_outlet',       fallback: 'Outlet',          icon: PercentIcon           },
    { key: 'giftVoucher', i18nKey: 'cfg_nav_header_toggle_gift_voucher', fallback: 'Gift voucher',    icon: CardGiftcardIcon      },
    { key: 'search',      i18nKey: 'cfg_nav_header_toggle_search',       fallback: 'Search',          icon: SearchIcon            },
    { key: 'account',     i18nKey: 'cfg_nav_header_toggle_account',      fallback: 'My account',      icon: AccountCircleIcon     },
    { key: 'cart',        i18nKey: 'cfg_nav_header_toggle_cart',         fallback: 'Cart',            icon: ShoppingCartOutlinedIcon },
];

/**
 * "Header content" card — sits at the top of the Navigation modal. All copy
 * goes through the i18n `t` prop; falls back to readable English if `t` is
 * missing so the card still renders when used outside MySft's i18n provider.
 */
const HeaderContentCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const get = (k, fb = false) => (value[k] !== undefined ? value[k] : fb);
    const set = (k) => (v) => onChange && onChange(k, v);

    return (
        <ConfigCard
            title={tr('cfg_nav_header_content_title', 'Header content')}
            description={tr('cfg_nav_header_content_desc', 'Choose which buttons appear in the top bar of your shop.')}
            theme={theme}
        >
            <Stack spacing={1}>
                {TOGGLE_ITEMS.map(({ key, i18nKey, fallback, icon: Icon }) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Icon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2">{tr(i18nKey, fallback)}</Typography>
                        </Box>
                        <Switch color="success"
                            size="small"
                            checked={!!get(key)}
                            onChange={(e) => set(key)(e.target.checked)}
                        />
                    </Box>
                ))}
            </Stack>

            <Box sx={{ borderTop: '1px dashed', borderColor: 'divider', pt: 1.5 }}>
                <Stack spacing={1}>
                    <FormControlLabel
                        control={
                            <Switch color="success"
                                size="small"
                                checked={!!get('searchExpandedByDefault')}
                                onChange={(e) => set('searchExpandedByDefault')(e.target.checked)}
                            />
                        }
                        label={<Typography variant="body2">{tr('cfg_nav_header_search_expanded', 'Show search field expanded by default')}</Typography>}
                    />
                    <FormControlLabel
                        control={
                            <Switch color="success"
                                size="small"
                                checked={!!get('showHomeButton')}
                                onChange={(e) => set('showHomeButton')(e.target.checked)}
                            />
                        }
                        label={<Typography variant="body2">{tr('cfg_nav_header_show_home', "Show 'Home' button to give live visitors a shortcut")}</Typography>}
                    />
                </Stack>
                {/* HOME_URL — destination of the home button. Only meaningful
                    when the home toggle is on, so it shows up indented when
                    the toggle is enabled. */}
                {get('showHomeButton') && (
                    <TextField
                        size="small"
                        label={tr('cfg_nav_header_home_url', 'Home button URL')}
                        value={get('homeUrl', '') || ''}
                        onChange={(e) => set('homeUrl')(e.target.value)}
                        fullWidth
                        sx={{ mt: 1.5, pl: 4 }}
                        placeholder="https://example.com"
                    />
                )}
            </Box>
        </ConfigCard>
    );
};

HeaderContentCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default HeaderContentCard;
